import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import { ToastContainer } from "react-toastify";

import i18n from "../../i18n";
import { SelectListDto } from "../../models";
import { SubjectCard, TableSpinner } from "../../components";
import { BreadCrumb, NoResultFound } from "../../components/Common";
import {
  LocalizedProperty,
  State,
  SubjectDto,
  UserProfileDto,
} from "../../interfaces";
import {
  deleteSubject,
  deleteSubjectConsent,
  getCourseSelectList,
  getGradeSelectListByCourse,
  getSubjectsByGrade,
  toggleSubjectState,
} from "../../store/actions";
import { CourseEnum, RoleEnum } from "../../enums";
import { AddEditGradeModal } from "../Grade/AddEditGradeModal";
import { AddEditSubjectModal } from "./AddEditSubjectModal";
import DeleteModal from "../../components/Common/DeleteModal";
import { CopyCutSubjectModal } from "./CopyCutSubjectModal";

type TProps = {};
export const SubjectsTabPage: React.FC<TProps> = () => {
  //#region hooks
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  //#endregion

  //#region redux state
  const { response: courseSelectList } = useSelector(
    (state: any): State<SelectListDto[]> => state.course.courseSelectList
  );
  const { loading: subjectsByGradeLoading, response: subjectsByGrade = [] } =
    useSelector(
      (state: any): State<SubjectDto[]> => state.subject.subjectsByGrade
    );
  const {
    loading: deleteSubjectLoading,
    error: deleteSubjectError,
    success: deleteSubjectSuccess,
  } = useSelector(
    (state: any): State<SubjectDto> => state.subject.deleteSubject
  );

  const { response: grades, loading: loadingGrades } = useSelector(
    (state: any): State<SelectListDto[]> => state.grade.gradeSelectListByCourse
  );

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );

  //#endregion

  //#region internal state
  const [showAddEditGradeModal, setShowAddEditGradeModal] = useState(false);
  const [editingGradeId, setEditingGradeId] = useState<string | null>(null);
  const [showAddEditSubjectModal, setShowAddEditSubjectModal] = useState(false);
  const [showCopyCutSubjectModal, setShowCopyCutSubjectModal] = useState(false);
  const [showDeleteSubjectModal, setShowDeleteSubjectModal] = useState(false);
  const [deletingSubjectId, setDeletingSubjectId] = useState<string | null>(
    null
  );
  const [copyingCutSubjectId, setCopyingCutSubjectId] = useState<string | null>(
    null
  );
  const [copyingCutSubjectName, setCopyingCutSubjectName] =
    useState<LocalizedProperty | null>(null);
  const [editingSubjectId, setEditingSubjectId] = useState<string | null>(null);
  const [course, setCourse] = useState<SelectListDto>();
  const [gradeId, setGradeId] = useState<string>();
  const [activeTab, setActiveTab] = useState<string>();
  const [action, setAction] = useState<string>("copy");

  //#endregion

  //#region effects

  useEffect(() => {
    if (courseSelectList && courseSelectList.length > 0) {
      let courseSelectListItem;
      if (window.location.pathname.includes(CourseEnum.FIRST_COURSE)) {
        courseSelectListItem = courseSelectList.find(
          (c) => c.value.en.toLowerCase() === "first course"
        );
      } else if (window.location.pathname.includes(CourseEnum.SECOND_COURSE)) {
        courseSelectListItem = courseSelectList.find(
          (c) => c.value.en.toLowerCase() === "second course"
        );
      }
      if (courseSelectListItem) {
        setCourse(courseSelectListItem);
        document.title = `${t("documentTitle", {
          pageName:
            courseSelectListItem.value[i18n.language === "ar" ? "ar" : "en"],
        })}`;
      }
    }
  }, [courseSelectList]);

  useEffect(() => {
    dispatch(getCourseSelectList());
  }, []);

  useEffect(() => {
    if (course?.id) {
      dispatch(getGradeSelectListByCourse(course?.id));
    }
  }, [course]);

  useEffect(() => {
    const hash = history.location.hash;
    if (hash && grades && grades.length > 0) {
      let id = hash.replace("#", "");
      setActiveTab(id);
      setGradeId(id);
      dispatch(getSubjectsByGrade(id));
    } else {
      if (grades && grades.length > 0) {
        setActiveTab(grades[0].id);
        setGradeId(grades[0].id);
        dispatch(getSubjectsByGrade(grades[0].id));
      }
    }
  }, [grades]);

  useEffect(() => {
    if (gradeId) dispatch(getSubjectsByGrade(gradeId));
  }, [gradeId]);

  useEffect(() => {
    if (!deleteSubjectLoading && deleteSubjectSuccess) {
      setDeletingSubjectId(null);
      setShowDeleteSubjectModal(false);
      dispatch(getSubjectsByGrade(gradeId as string));
    }
  }, [deleteSubjectLoading]);

  //#endregion

  //#region handlers

  const onAddSubject = () => {
    if (course && course.id && gradeId) {
      setShowAddEditSubjectModal(true);
    }
  };

  const onEditSubject = (id: string) => {
    if (course && course.id && gradeId) {
      setEditingSubjectId(id);
      setShowAddEditSubjectModal(true);
    }
  };

  const onToggleState = (id: string) => {
    if (course && course.id && gradeId) {
      dispatch(toggleSubjectState(id as string, history));
    }
  };

  const onDeleteSubject = (id: string) => {
    setDeletingSubjectId(id);
    setShowDeleteSubjectModal(true);
    dispatch(deleteSubjectConsent(id));
  };

  const onCopySubject = (id: string) => {
    setCopyingCutSubjectName(
      subjectsByGrade?.find((s) => s.id === id)?.name || null
    );
    setCopyingCutSubjectId(id);
    setShowCopyCutSubjectModal(true);
    setAction("copy");
  };

  const onCutSubject = (id: string) => {
    setCopyingCutSubjectName(
      subjectsByGrade?.find((s) => s.id === id)?.name || null
    );
    setCopyingCutSubjectId(id);
    setShowCopyCutSubjectModal(true);
    setAction("cut");
  };

  const onDeleteSubjectConfirm = () => {
    dispatch(deleteSubject(deletingSubjectId as string, history));
    setDeletingSubjectId(null);
  };

  const toggleTab = (changingTab: any) => {
    if (activeTab !== changingTab) {
      setActiveTab(changingTab);
      setGradeId(changingTab);
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("subjects")}
            pageTitle={t(
              i18n.language === "ar"
                ? course?.value.ar || ""
                : course?.value.en || ""
            )}
          />
          {!loadingGrades && grades && grades.length > 0 && (
            <React.Fragment>
              <Row>
                <Col xxl={12}>
                  <Card>
                    <CardBody>
                      <Nav pills className="nav-success">
                        {grades?.map((grade: SelectListDto, index: number) => (
                          <NavItem key={index}>
                            <NavLink
                              href={`#${grade.id}`}
                              style={{ cursor: "pointer" }}
                              className={classnames({
                                active: activeTab === grade.id,
                              })}
                              onClick={() => {
                                toggleTab(grade.id);
                              }}
                            >
                              {i18n.language === "ar"
                                ? grade.value.ar
                                : grade.value.en}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              {profile && profile.role.key == RoleEnum.SUPER_ADMIN && (
                <Row className="g-2">
                  <Col className="col-sm-auto ms-auto">
                    <div className="list-grid-nav hstack gap-1">
                      {gradeId && (
                        <Button
                          color="success"
                          onClick={() => {
                            onAddSubject();
                          }}
                        >
                          <i className="ri-add-fill me-1 align-bottom"></i>
                          {t("addItem", {
                            item: "subject",
                          })}
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
              <Row className="mt-2">
                <Col xxl={12}>
                  <TabContent activeTab={activeTab}>
                    {grades &&
                      grades.length > 0 &&
                      grades?.map((grade: SelectListDto, index: number) => (
                        <TabPane
                          tabId={grade.id}
                          id={`${grade.id}`}
                          key={index}
                        >
                          <React.Fragment>
                            {gradeId && (
                              <CardBody>
                                <Col lg={12}>
                                  <Row className="team-list grid-view-filter">
                                    {!subjectsByGradeLoading &&
                                      subjectsByGrade &&
                                      subjectsByGrade.length > 0 &&
                                      subjectsByGrade.map(
                                        (item: SubjectDto, key) => (
                                          <Col key={key}>
                                            <SubjectCard
                                              link={`/subjects/details/${item.id}`}
                                              {...item}
                                              sequenceNumber={
                                                item.subjectCourseDuration &&
                                                item.subjectCourseDuration
                                                  .length > 0
                                                  ? item
                                                      .subjectCourseDuration[0]
                                                      .sequenceNumber
                                                  : 0
                                              }
                                              onEditSubject={onEditSubject}
                                              onDeleteSubject={onDeleteSubject}
                                              onCopySubject={onCopySubject}
                                              onCutSubject={onCutSubject}
                                              onToggleState={onToggleState}
                                            />
                                          </Col>
                                        )
                                      )}
                                    {subjectsByGradeLoading && (
                                      <CardBody>
                                        <TableSpinner />
                                      </CardBody>
                                    )}
                                    {!subjectsByGradeLoading &&
                                      (!subjectsByGrade ||
                                        subjectsByGrade?.length === 0) && (
                                        <CardBody>
                                          <NoResultFound />
                                        </CardBody>
                                      )}
                                  </Row>
                                </Col>
                              </CardBody>
                            )}
                          </React.Fragment>
                        </TabPane>
                      ))}
                  </TabContent>
                </Col>
              </Row>
            </React.Fragment>
          )}
          {loadingGrades && <TableSpinner />}
          {!loadingGrades && (!grades || grades.length === 0) && (
            <NoResultFound />
          )}
        </Container>
      </div>
      <AddEditGradeModal
        courseId={course?.id || ""}
        id={editingGradeId}
        show={showAddEditGradeModal}
        onCloseClick={() => {
          setShowAddEditGradeModal(false);
          setEditingGradeId(null);
        }}
      />
      <AddEditSubjectModal
        gradeId={gradeId || ""}
        courseId={course?.id || ""}
        id={editingSubjectId}
        show={showAddEditSubjectModal}
        onCloseClick={() => {
          setShowAddEditSubjectModal(false);
          setEditingSubjectId(null);
        }}
      />
      <CopyCutSubjectModal
        id={copyingCutSubjectId as string}
        name={copyingCutSubjectName}
        show={showCopyCutSubjectModal}
        action={action}
        onCloseClick={() => {
          setShowCopyCutSubjectModal(false);
          setCopyingCutSubjectId(null);
          if (gradeId) dispatch(getSubjectsByGrade(gradeId));
        }}
      />

      <DeleteModal
        loading={deleteSubjectLoading}
        show={showDeleteSubjectModal}
        onDeleteClick={() => onDeleteSubjectConfirm()}
        onCloseClick={() => {
          setShowDeleteSubjectModal(false);
        }}
        error={deleteSubjectError}
      />
    </React.Fragment>
  );
};
